import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, type ButtonVariant } from './Button.js';

/**
 * Reusable confirm dialog — a centered modal over a dimmed backdrop, built from the shared Button +
 * design tokens. First used for the §1.4 "apply margin to all clients" gate in Settings; reusable by
 * any destructive/bulk confirm (e.g. the per-client service slice). Caller owns open state, the
 * confirm action, and `busy` (shows a spinner on the confirm button and blocks backdrop-dismiss).
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  busy = false,
  confirmDisabled = false,
  size = 'md',
  dismissOnBackdrop = true,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: ButtonVariant;
  busy?: boolean;
  /** Disables the confirm button without the busy spinner — e.g. gating on a required checkbox. */
  confirmDisabled?: boolean;
  /** Body width. 'md' (default) is every existing caller, unchanged. 'lg' is for dialogs that carry a
   *  DATA BLOCK rather than a sentence — e.g. the #72 void dialog enumerating a payment's allocations,
   *  where 'md' wrapped every row onto two lines and pushed the body into a scroll it did not need. */
  size?: 'md' | 'lg';
  /** A click on the dimmed backdrop cancels (the default). FALSE for a dialog the person must answer with a
   *  button: one that may not come back on its own if dismissed by accident (the 04 #160 WS-C M2b lapse
   *  dialog, owner rule s249) - the Cancel button is then the only way out. */
  dismissOnBackdrop?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="absolute inset-0 glass-scrim"
        onClick={busy || !dismissOnBackdrop ? undefined : onCancel}
        aria-hidden="true"
      />
      <div className={`relative flex max-h-[90dvh] w-full ${size === 'lg' ? 'max-w-lg' : 'max-w-md'} flex-col overflow-hidden rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] shadow-xl`}>
        <h2 className="shrink-0 px-6 pb-2 pt-6 font-display text-xl text-[var(--color-text)]">{title}</h2>
        {children ? (
          // Body padding was `py-1` (4px). That left the last control — usually a required text input —
          // sitting 4px off the footer rule, which read as a layout bug rather than a tight style, and
          // it made the body's own scroll offset highly visible: any scroll at all looked like the
          // spacing had changed. `pt-2 pb-4` gives the body a stable, deliberate gutter above the rule
          // in EVERY dialog, and makes a residual scroll offset far less noticeable.
          <div className="flex-1 space-y-2 overflow-y-auto px-6 pb-4 pt-2 text-sm text-[var(--color-text)]">{children}</div>
        ) : null}
        <div className="flex shrink-0 justify-end gap-3 border-t border-[rgb(var(--edge-rgb)/0.05)] px-6 py-4">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button type="button" variant={confirmVariant} onClick={onConfirm} loading={busy} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
